import { uiText } from '../i18n/uiText';
import React, { useEffect, useState } from 'react';
import {
  Sliders, RotateCcw, Save, CheckCircle2, AlertCircle, Sparkles,
} from 'lucide-react';
import { selfCheckWeightsApi, getApiErrorMessage } from '../api';
import { useConfirm } from '../store/ConfirmContext';
import { LoadingState } from '../components/common/UI';
import PageIntro from '../components/ui/PageIntro';
import { useAuth } from '../store/AuthContext';
import useDraftBuffer from '../hooks/useDraftBuffer';
import FormDraftNotice from '../components/common/FormDraftNotice';

const BLOCK_LABELS = {
  legal: { label: uiText("Юридическая состоятельность"), color: '#1d4ed8', bg: '#dbeafe' },
  financial: { label: uiText("Финансовая устойчивость"), color: '#7e22ce', bg: '#f3e8ff' },
  reputation: { label: uiText("Репутация и опыт"), color: '#15803d', bg: '#dcfce7' },
};

export default function SelfCheckWeightsPage() {
  const confirm = useConfirm();
  const [config, setConfig] = useState(null);
  const { user } = useAuth();
  const buffer = useDraftBuffer(user?.id, 'settings', 'self-check-weights');
  const editedWeights = buffer.values.weights || {};
  const thresholdGreen = buffer.values.green ?? 80;
  const thresholdYellow = buffer.values.yellow ?? 50;
  const edit = (name, value) => { if (!busy && !buffer.recovery) buffer.edit(name, value); };
  const setEditedWeights = value => edit('weights', value);
  const setThresholdGreen = value => edit('green', value);
  const setThresholdYellow = value => edit('yellow', value);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    setError('');
    try {
      const res = await selfCheckWeightsApi.get();
      setConfig(res.data);
      const w = {};
      res.data.questions.forEach(q => { w[q.id] = q.current_weight; });
      if (buffer.active) buffer.hydrate({ weights: w, green: res.data.threshold_green, yellow: res.data.threshold_yellow });
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalWeight = Object.values(editedWeights).reduce((sum, v) => sum + (Number(v) || 0), 0);

  const save = async () => {
    if (busy || loading || buffer.recovery) return;
    const snapshot = buffer.snapshot();
    if (thresholdGreen <= thresholdYellow) {
      setError(uiText("Порог \"зелёный\" должен быть строго больше \"жёлтого\""));
      return;
    }
    setBusy(true); setError(''); setSuccess('');
    try {
      await selfCheckWeightsApi.update({
        weights: editedWeights,
        threshold_green: Number(thresholdGreen),
        threshold_yellow: Number(thresholdYellow),
      });
      buffer.acknowledge(snapshot, { committed: true });
      setSuccess(uiText("Настройки сохранены"));
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (e) { setError(getApiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (busy) return;
    if (!await confirm(uiText("Сбросить все настройки к значениям по умолчанию?"), { title: uiText("Сброс настроек"), confirmLabel: uiText("Сбросить") })) return;
    setBusy(true); setError('');
    try {
      await selfCheckWeightsApi.reset();
      buffer.discard();
      setSuccess(uiText("Сброшено к дефолтам"));
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (e) { setError(getApiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <LoadingState message={uiText("Загружаем настройки Self-check…")} />;
  if (!config) return null;

  const grouped = { legal: [], financial: [], reputation: [] };
  config.questions.forEach(q => { if (grouped[q.block]) grouped[q.block].push(q); });

  return (
    <div className="page-content self-check-weights-page">
      <FormDraftNotice buffer={buffer} label={uiText("Настройки Self-check")} disabled={busy} />
      <PageIntro
        icon={Sliders}
        title={uiText("Настройка весов Self-check")}
        description={uiText("Веса вопросов и пороги рейтинга применяются ко всем новым расчётам после сохранения.")}
        aside={(
          <button type="button" className="btn btn-outline" onClick={reset} disabled={busy}>
            <RotateCcw size={14}/>{uiText(" Сбросить по умолчанию")}</button>
        )}
      />

      {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>{error}</div>}
      {success && (
        <div className="alert" style={{
          background: '#dcfce7', border: '1px solid #86efac', color: '#15803d',
          marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8,
        }}><CheckCircle2 size={16}/> {success}</div>
      )}

      {/* Сводка */}
      <div className="card" style={{ marginBottom: 16, overflow: 'hidden' }}>
        <div className="self-check-weights-summary" style={{
          padding: 18,
          background: 'linear-gradient(90deg, #ede9fe 0%, white 70%)',
          display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, background: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Sparkles size={22} style={{ color: '#7c3aed' }}/>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 12, color:'var(--premium-text-secondary)' }}>{uiText("Сумма всех весов")}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#5b21b6' }}>
              {totalWeight}{' '}
              <span style={{ fontSize: 13, fontWeight: 500, color:'var(--premium-text-secondary)' }}>{uiText("(нормализуется к 100)")}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <ThresholdInput label={uiText("Зелёный ≥")} value={thresholdGreen} onChange={setThresholdGreen}/>
            <ThresholdInput label={uiText("Жёлтый ≥")} value={thresholdYellow} onChange={setThresholdYellow}/>
            <div style={{ fontSize: 11, color:'var(--premium-text-secondary)', alignSelf: 'center', maxWidth: 200 }}>{uiText("ниже жёлтого порога — красный")}</div>
          </div>
        </div>
      </div>

      {Object.entries(grouped).map(([blockKey, qs]) => {
        const m = BLOCK_LABELS[blockKey];
        return (
          <div key={blockKey} className="card" style={{ marginBottom: 14, overflow: 'hidden' }}>
            <div className="self-check-weights-block-header" data-block={blockKey} style={{
              padding: '12px 18px', background: m.bg, borderBottom: `1px solid ${m.bg}`,
              fontWeight: 700, color: m.color,
            }}>{m.label}</div>
            <div>
              {qs.map((q, idx) => {
                const cur = editedWeights[q.id] ?? q.default_weight;
                const changed = cur !== q.default_weight;
                return (
                  <div key={q.id} style={{
                    padding: '12px 18px',
                    borderBottom: idx < qs.length - 1 ? '1px solid #f1f5f9' : 'none',
                    display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                  }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                      background: '#f1f5f9', color:'var(--premium-text-secondary)',
                      fontWeight: 700, fontSize: 12,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>{q.order}</div>
                    <div style={{ flex: 1, minWidth: 250 }}>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>
                        {q.title}
                        {q.required && <span style={{ color: '#b91c1c', marginLeft: 4 }}>*</span>}
                      </div>
                      <div style={{ fontSize: 11, color:'var(--premium-text-secondary)', marginTop: 2 }}>{uiText("Дефолт: ")}{q.default_weight}{changed && uiText(" · сейчас: {v0}", {v0: (cur)})}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        className="self-check-weight-range"
                        type="range" min="0" max="30" step="1"
                        value={cur}
                        onChange={e => setEditedWeights({...editedWeights, [q.id]: Number(e.target.value)})}
                        style={{
                          width: 120,
                          '--range-progress': `${Math.max(0, Math.min(100, (Number(cur) / 30) * 100))}%`,
                        }}
                      />
                      <input
                        type="number" min="0" max="100"
                        value={cur}
                        onChange={e => setEditedWeights({...editedWeights, [q.id]: Number(e.target.value)})}
                        style={{
                          width: 60, padding: '4px 8px', borderRadius: 6,
                          border: changed ? '1px solid #7c3aed' : '1px solid #e2e8f0',
                          textAlign: 'center', fontWeight: 600,
                          color: changed ? '#7c3aed' : '#1e293b',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div style={{
        position: 'sticky', bottom: 0, marginTop: 16, padding: 14, borderRadius: 12,
        background: 'white', border: '1px solid #e2e8f0',
        boxShadow: '0 -4px 12px -2px rgba(0,0,0,0.05)',
        display: 'flex', gap: 10, justifyContent: 'flex-end',
      }}>
        <button type="button" className="btn btn-outline" onClick={load} disabled={busy}>{uiText("Отменить изменения")}</button>
        <button type="button" className="btn btn-primary" onClick={save} disabled={busy}
          style={{ background: '#7c3aed', borderColor: '#7c3aed' }}>
          <Save size={14}/>{uiText(" Сохранить")}</button>
      </div>
    </div>
  );
}

function ThresholdInput({ label, value, onChange }) {
  return (
    <div>
      <div style={{ fontSize: 11, color:'var(--premium-text-secondary)', marginBottom: 4 }}>{label}</div>
      <input type="number" min="1" max="100" value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{
          width: 70, padding: '4px 8px', borderRadius: 6,
          border: '1px solid #e2e8f0', textAlign: 'center', fontWeight: 700,
        }}/>
    </div>
  );
}
